<template>
  <div class="pw-memberships">
    <!-- Edit dialog -->
    <v-dialog      v-model="showEditDialog"
      min-width="600"
      max-width="900"
      min-height="600"
      max-height="900"
      @keydown.esc="closeEditDialog"
      @click:outside="closeEditDialog"
    >
      <membership-manager
        v-if="showEditDialog && activeMembership"
        :membership="activeMembership"
        @close-dialog="closeEditDialog"
        @saved="closeEditDialog"
        @error="onManagerError"
      ></membership-manager>
    </v-dialog>

    <!-- Deactivate dialog -->
    <v-dialog      v-model="showDeactivateDialog"
      min-width="600"
      max-width="900"
      min-height="600"
      max-height="900"
      @keydown.esc="closeDeactivateDialog"
    >
      <deactivate-membership
        @close-dialog="closeDeactivateDialog"
        :membership="selectedMembership"
      ></deactivate-membership>
    </v-dialog>

    <v-dialog      v-model="planIsOpen"
      min-width="600"
      max-width="900"
      min-height="600"
      max-height="900"
      @keydown.esc="planIsOpen = false"
    >
      <plan-details-maintainer
        :content="selectedMembership"
        @close-dialog="closePlanDialog"
      ></plan-details-maintainer>
    </v-dialog>

    <!-- Clipboard copy snackbar -->
    <v-snackbar v-model="copyContentSnackbar">
      {{ copyContentMessage }}

      <template v-slot:action="{ attrs }">
        <v-btn color="pink" text v-bind="attrs" @click="copyContentSnackbar = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Error snackbar (gestor de membresía) -->
    <v-snackbar v-model="errorSnackbar" color="error" :timeout="5000">
      {{ errorSnackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="errorSnackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

    <!-- Feedback snackbar (utilsMixin) -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="4000" top>
      {{ snackbarMessage }}
    </v-snackbar>

    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Membresías</h1>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="memberships"
      :options.sync="options"
      :loading="loadingData"
      :items-per-page="limit"
      :server-items-length="totalItems"
      :sort-desc.sync="sort"
      :disable-sort="loadingData"
      multi-sort
      class="elevation-1"
      :footer-props="{
        'items-per-page-options': [5, 10, 20, 30, 40, 50, 500, 999]
      }"
    >
      <template v-slot:top>
        <v-row class="px-5 py-5" style="justify-content: space-between; align-items: center">
          <v-col cols="12" md="6" class="my-0 py-0">
            <v-text-field
              v-model="stringToSearch"
              label="Buscar"
              :disabled="loadingData"
              placeholder="Referencia, mascota, plan..."
              dense
              hide-details
              outlined
              append-icon="mdi-magnify"
              @click:append="searchString"
              @keyup="cleanSearch"
              @keyup.enter="searchString"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6" class="my-0 py-0">
            <v-row class="ma-0 align-center justify-end">
              <v-btn v-if="uniqueUser" color="primary" rounded @click="seeAllMemberships()">Ver todas</v-btn>
              <template v-if="!uniqueUser">
                <v-autocomplete
                  v-model="contratoSelected"
                  :items="contratos"
                  :item-text="contratoLabel"
                  item-value="id"
                  label="Buscar contrato..."
                  prepend-inner-icon="mdi-magnify"
                  return-object
                  clearable
                  dense
                  hide-details
                  :loading="loadingContratos"
                  style="max-width: 280px;"
                ></v-autocomplete>
              </template>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="px-5 pt-0 pb-5">
          <v-chip-group v-model="search" column multiple class="mx-3">
            <v-chip
              filter
              outlined
              v-for="status in statuses"
              v-longclick="() => longPressChip(status)"
              :key="status.name"
              :color="status.color"
              :disabled="loadingData"
              :value="status.value"
            >
              {{ status.name }}
            </v-chip>
          </v-chip-group>
        </v-row>
      </template>
      <template v-slot:item.user="{ item }">
        <span
          v-if="item.status === 2 && (item.contractUrl == '' || item.contractUrl == null)"
          style="color: red"
          ><strong> * </strong></span
        >
        <span>{{ item.user.name || '' }} {{ item.user.surname || '' }}</span>
      </template>

      <template v-slot:item.pet="{ item }">
        <v-chip
          v-if="item.pet.species === 'cat'"
          small
          color="green"
          class="white--text font-weight-bold"
          ><v-icon left> mdi-cat </v-icon> {{ item.pet.name }}
        </v-chip>
        <v-chip
          v-if="item.pet.species === 'dog'"
          small
          color="orange"
          class="white--text font-weight-bold"
          ><v-icon left> mdi-dog </v-icon>{{ item.pet.name }}</v-chip
        >
      </template>

      <template v-slot:item.plan.name="{ item }">
        <v-col class="ma-0 pa-0 mt-1">
          <template>
            <span
              @click="openPlanDialog(item)"
              class="font-weight-medium"
              style="cursor: pointer"
            >
              {{ item.plan.name }}
            </span>
          </template>

          <v-chip-group>
            <v-chip outlined color="primary" x-small class="white--text font-weight-bold">{{
              item.paymentInterval === 1 ? 'Anual' : 'Mensual'
            }}</v-chip>
          </v-chip-group>
        </v-col>
      </template>
      <template v-slot:item.startDate="{ item }">
        <span v-if="item.status !== 3 && item.status !== 5">{{ item.startDate | date }}</span>
        <span v-else>—</span>
      </template>
      <template v-slot:item.endDate="{ item }">
        <span v-if="item.endDate">{{ item.endDate | date }}</span>
        <span v-else>—</span>
      </template>
      <template v-slot:item.status="{ item }">
        <membership-status-chip :status="item.status"></membership-status-chip>
      </template>

      <template v-slot:item.nextInvoiceDate="{ item }">
        <span v-if="item.nextInvoiceDate">{{ item.nextInvoiceDate | date }}</span>
        <span v-else>—</span>
      </template>
      <template v-slot:item.actions="props">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="mx-1"
              depressed
              x-small
              fab
              color="green"
              v-bind="attrs"
              v-on="on"
              @click="editMembership(props.item)"
            >
              <v-icon small color="white">mdi-pencil</v-icon>
            </v-btn>
          </template>
          <span>Revisar</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="props.item.contractUrl !== null && props.item.contractUrl !== ''"
              class="mx-1"
              x-small
              depressed
              fab
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="showPolicy(props.item.contractUrl)"
            >
              <v-icon small color="white">mdi-text-box-search-outline</v-icon>
            </v-btn>
          </template>
          <span>Ver Contrato</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="props.item.status != 4"
              class="mx-1"
              x-small
              depressed
              fab
              color="error"
              v-bind="attrs"
              v-on="on"
              @click="deactivateMembership(props.item)"
            >
              <v-icon small color="white">mdi-close-octagon</v-icon>
            </v-btn>
          </template>
          <span>Desactivar</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="props.item.status == 2"
              class="mx-1"
              x-small
              depressed
              fab
              color="grey"
              v-bind="attrs"
              v-on="on"
              @click="copyContent(props.item)"
            >
              <v-icon small color="white">mdi-content-copy</v-icon>
            </v-btn>
          </template>
          <span>Copiar datos</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <span style="color: red; font-size: 12px"
      ><strong>*</strong> : Este usuario no tiene contrato</span
    >
    <overlay :value="loadingOverlay"></overlay>
  </div>
</template>

<script>
// import moment from 'moment';
import MembershipManager from '../components/MembershipManager.vue';
import MembershipStatusChip from '../components/MembershipStatusChip';
import DeactivateMembership from '../components/DeactivateMembership';
// import FeaturesModal from '../components/FeaturesModal';
import Overlay from '../components/Overlay.vue';
import MembershipsService from '../services/memberships.service';
import DateUtils from '../utils/date.utils';
import MiscUtils from '../utils/misc.utils';
import PlanDetailsMaintainer from '../components/PlanDetailsMaintainer.vue';
import ContratoService from '../services/contrato.service';
import { utilsMixin } from '../mixins/utilsMixin';

export default {
  mixins: [utilsMixin],
  components: {
    MembershipManager,
    MembershipStatusChip,
    DeactivateMembership,
    Overlay,
    PlanDetailsMaintainer
    // FeaturesModal
  },
  data() {
    return {
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'green',
      uniqueUser: false,
      showDialog: false,
      showEditDialog: false,
      showDeactivateDialog: false,
      loadingData: true,
      loadingOverlay: false,
      options: {},
      users: [],
      memberships: [],
      selectedUser: null,
      selectedMembership: null,
      activeMembership: null,
      limit: 10,
      page: 1,
      totalItems: 0,
      sort: [],
      search: [2, 4, 5, 6],
      stringToSearch: null,
      statuses: [
        {
          name: 'Activo',
          value: 2,
          color: 'green'
        },

        {
          name: 'Desactivado',
          value: 4,
          color: 'red'
        },
        {
          name: 'Pendiente de pago',
          value: 5,
          color: 'primary'
        },
        {
          name: 'En revisión',
          value: 6,
          color: 'orange'
        }
      ],
      headers: [
        {
          text: 'Cliente',
          value: 'user',
          sortable: false
        },
        {
          text: 'Mascota',
          value: 'pet',
          sortable: false
        },
        {
          text: 'Referencia',
          value: 'ref',
          sortable: true
        },
        {
          text: 'Plan',
          value: 'plan.name',
          sortable: false
        },
        {
          text: 'Inicio Membresía',
          value: 'startDate',
          sortable: false
        },
        {
          text: 'Fecha de término',
          value: 'endDate',
          sortable: true
        },
        // {
        //   text: 'Intervalo',
        //   value: 'paymentInterval',
        //   sortable: false
        // },
        {
          text: 'Próximo pago',
          value: 'nextInvoiceDate',
          sortable: true
        },
        /*{
          text: 'Días gratis',
          value: 'finishTrialDate',
          sortable: true
        },*/
        {
          text: 'Estado',
          value: 'status',
          sortable: true
        },

        {
          text: 'Acciones',
          value: 'actions',
          sortable: false
        }
      ],
      userId: null,
      copyContentSnackbar: false,
      errorSnackbar: false,
      errorSnackbarMessage: '',
      copyContentMessage: '',
      planIsOpen: false,
      contratos: [],
      contratoSelected: null,
      loadingContratos: false
    };
  },
  beforeMount() {
    if (this.$route.params.userId) {
      this.userId = this.$route.params.userId;
      this.uniqueUser = true;
    }
  },
  mounted() {
    this.getContratos();
  },
  created() {
    if (this.$route.params.userId) {
      this.userId = this.$route.params.userId;
      this.uniqueUser = true;
    }
  },
  watch: {
    options: {
      handler() {
        this.page = this.options.page;
        this.limit = this.options.itemsPerPage;
        let sort;
        if (this.options.sortBy.length !== 0) {
          sort = this.options.sortBy.map(s => {
            const index = this.options.sortBy.indexOf(s);
            return this.options.sortDesc[index] == true ? `-${s}` : s;
          });
        }
        this.getMemberships(this.page, this.limit, this.search, sort);
      },
      deep: true
    },
    search: {
      handler() {
        this.page = 1;
        this.limit = 10;
        this.sort = [];
        this.getMemberships(this.page, this.limit, this.search, this.sort);
      },
      deep: true
    },
    contratoSelected() {
      this.page = 1;
      this.limit = 10;
      this.sort = [];
      this.getMemberships(this.page, this.limit, this.search, this.sort);
    },
  },
  methods: {
    openPlanDialog(membership) {
      if (membership) {
        this.planIsOpen = true;
        this.selectedMembership = membership;
      }
    },
    closePlanDialog() {
      this.planIsOpen = false;
    },
    openDialog(user) {
      this.showDialog = true;
      this.selectedUser = user;
    },
    closeDialog() {
      this.showDialog = false;
      this.selectedUser = null;
      this.getMemberships(this.page, this.limit, this.search, this.sort);
      // this.getUsers(this.page, this.limit);
    },
    closeEditDialog() {
      this.showEditDialog = false;
      this.activeMembership = null;
      this.getMemberships(this.page, this.limit, this.search, this.sort);
    },
    closeDeactivateDialog() {
      this.showDeactivateDialog = false;
      this.selectedMembership = null;
      this.getMemberships(this.page, this.limit, this.search, this.sort);
    },
    async getMemberships(page, limit, query, sort) {
      this.loadingData = true;
      const res = await MembershipsService.getMemberships(
        page,
        limit,
        query,
        sort,
        this.userId,
        this.stringToSearch && this.stringToSearch.length >= 3 ? this.stringToSearch : null,
        null,
        this.contratoSelected && this.contratoSelected.id ? this.contratoSelected.id : null
      );
      if (res.status === 'ok') {
        this.memberships = res.data.memberships;
        this.totalItems = res.data.totalItems;
      }
      this.loadingData = false;
    },

    editMembership(membership) {
      this.showEditDialog = true;
      this.activeMembership = membership;
    },
    onManagerError(message) {
      this.errorSnackbarMessage = message || 'Ocurrió un error';
      this.errorSnackbar = true;
    },
    deactivateMembership(membership) {
      this.showDeactivateDialog = true;
      this.selectedMembership = membership;
    },
    showPolicy(url) {
      window.open(url);
    },
    getRemainingTrialDaysString(membership) {
      return DateUtils.getRemainingTrialDaysString(membership);
    },
    searchString() {
      if (
        (this.stringToSearch && this.stringToSearch.length >= 3) ||
        !this.stringToSearch ||
        this.stringToSearch.length == 0
      ) {
        this.sort = [];
        this.getMemberships(this.page, this.limit, this.search, this.sort);
      }
    },
    cleanSearch() {
      if (!this.stringToSearch || this.stringToSearch.length == 0) {
        this.sort = [];
        this.getMemberships(this.page, this.limit, this.search, this.sort);
      }
    },
    seeAllMemberships() {
      // Not `/`: the root now resolves the admin's default section (spec 028).
      this.$router.replace({ path: '/memberships' });
      this.userId = null;
      this.uniqueUser = false;
      this.getMemberships(this.page, this.limit, this.search, this.sort);
    },
    longPressChip(status) {
      // Si ya están seleccionados todos los estados excepto el actual
      if (this.search.length === this.statuses.length - 1 && !this.search.includes(status.value)) {
        // Selecciona solo el estado actual
        this.search = [status.value];
      } else {
        // Selecciona todos los estados excepto el actual
        this.search = this.statuses.filter(s => s.value !== status.value).map(s => s.value);
      }
    },

    async getContratos() {
      this.loadingContratos = true;
      const res = await ContratoService.getAll();
      if (res.status === 'ok') {
        this.contratos = (res.data || [])
          .filter(c => c.status === 'Activo')
          .sort((a, b) => this.contratoLabel(a).localeCompare(this.contratoLabel(b), 'es'));
      } else {
        this.showErrorSnackbar('Error al cargar los contratos');
      }
      this.loadingContratos = false;
    },

    contratoLabel(c) {
      const empresa = c.company && c.company.name;
      const producto = c.producto && c.producto.nombre;
      const parts = [empresa, c.codigo, producto].filter(Boolean);
      return parts.join(' — ');
    },

    copyContent(membership) {
      const user = membership.user;
      const pet = membership.pet;
      const data =
        `Nombre tutor: ${user.name} ${user.surname}\n` +
        `Dirección: ${user.address?.street} ${user.address?.number} ${user.address?.extra}\n` +
        `${user.address?.commune?.name}, ${user.address?.region?.name}\n` +
        `Teléfono contacto: ${user.phone}\n` +
        `Nombre mascota: ${pet.name}\n` +
        `Especie: ${pet.species == 'cat' ? 'Gato' : 'Perro'}\n` +
        `Peso mascota: \n` +
        `Motivo consulta: \n` +
        `Costo cliente: `;
      const copyResult = MiscUtils.copyToClipboard(data);
      this.copyContentSnackbar = true;
      this.copyContentMessage = copyResult
        ? 'Se copiaron los datos al portapapeles'
        : 'Hubo un error al copiar los datos';
    }
  }
};
</script>

<style>
/* Acotado a .pw-memberships: sin el prefijo alineaba el contenido de todo
   v-slide-group / v-tabs de la app. */
.pw-memberships .v-slide-group__content {
  align-items: center !important;
}
</style>
